import React from "react";
import {
  TableContainer,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Box,
  Card,
  CardContent,
  Typography,
  useMediaQuery,
  useTheme,
  Chip,
} from "@mui/material";

const AlertTable = ({ riskAssessmentData }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  if (!riskAssessmentData || riskAssessmentData.length === 0) {
    return (
      <Box
        display="flex"
        alignItems="center"
        justifyContent="center"
        minHeight="200px"
        p={2}
      >
        <Typography variant="h6" color="text.secondary">
          No Risk Found
        </Typography>
      </Box>
    );
  }

  const getRiskColor = (level) => {
    switch (level) {
      case "High":
        return { bg: "#f8d7da", chip: "error" };
      case "Moderate":
        return { bg: "#fff3cd", chip: "warning" };
      case "Low":
        return { bg: "#d4edda", chip: "success" };
      default:
        return { bg: "#f5f5f5", chip: "default" };
    }
  };

  // Mobile Card View
  if (isMobile) {
    return (
      <Box sx={{ p: 1 }}>
        {riskAssessmentData.map((item, idx) => {
          const riskColors = getRiskColor(item["Risk Level"]);

          return (
            <Card
              key={idx}
              sx={{
                mb: 2,
                backgroundColor: riskColors.bg,
                border: "1px solid",
                borderColor: "divider",
              }}
              elevation={1}
            >
              <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
                <Box
                  sx={{
                    mb: 2,
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "flex-start",
                    gap: 1,
                  }}
                >
                  <Chip
                    label={item["Risk Level"]}
                    color={riskColors.chip}
                    size="small"
                    sx={{ fontWeight: "bold" }}
                  />
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{
                      backgroundColor: "rgba(0,0,0,0.05)",
                      px: 1,
                      py: 0.5,
                      borderRadius: 1,
                      fontSize: "0.75rem",
                    }}
                  >
                    {item["Complication Area"]}
                  </Typography>
                </Box>

                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 500,
                    lineHeight: 1.4,
                    color: "text.primary",
                  }}
                >
                  {item.Notes}
                </Typography>
              </CardContent>
            </Card>
          );
        })}
      </Box>
    );
  }

  // Desktop Table View
  return (
    <TableContainer component={Paper} variant="outlined">
      <Table size="small" sx={{ minWidth: 650 }}>
        <TableHead>
          <TableRow sx={{ backgroundColor: "#f5f5f5" }}>
            <TableCell sx={{ fontWeight: "bold", width: "50%" }}>
              Note
            </TableCell>
            <TableCell sx={{ fontWeight: "bold", width: "20%" }}>
              Risk Level
            </TableCell>
            <TableCell sx={{ fontWeight: "bold", width: "30%" }}>
              Complication Area
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {riskAssessmentData.map((item, idx) => {
            const riskColors = getRiskColor(item["Risk Level"]);

            return (
              <TableRow
                key={idx}
                hover
                sx={{
                  backgroundColor: riskColors.bg,
                  "&:hover": {
                    backgroundColor: `${riskColors.bg}CC`, // slightly more opaque on hover
                  },
                }}
              >
                <TableCell>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {item.Notes}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Chip
                    label={item["Risk Level"]}
                    color={riskColors.chip}
                    size="small"
                    sx={{ fontWeight: "bold" }}
                  />
                </TableCell>
                <TableCell>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {item["Complication Area"]}
                  </Typography>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default AlertTable;
